/**
 * Text-to-Speech Hook
 * Provides functionality to convert text to speech using the Web Speech API
 */

import { useState, useCallback, useEffect } from 'react'

/**
 * Voice settings configuration
 */
interface VoiceSettings {
  /** Name of the voice to use */
  voice?: string
  /** Speech rate (0.1 to 10, default 1) */
  speed?: number
  /** Speech pitch (0 to 2, default 1) */
  pitch?: number
}

/**
 * Return type for the useTextToSpeech hook
 */
interface UseTextToSpeechReturn {
  /** Start speaking the provided text */
  speak: (text: string) => void
  /** Stop any current speech */
  stop: () => void
  /** Whether speech is currently playing */
  isSpeaking: boolean
  /** Whether the browser supports speech synthesis */
  isSupported: boolean
}

/**
 * Hook for text-to-speech functionality using Web Speech API
 *
 * @param voiceSettings - Optional voice configuration
 * @returns Object with speak, stop, isSpeaking, and isSupported
 *
 * @example
 * ```tsx
 * const { speak, stop, isSpeaking, isSupported } = useTextToSpeech({ speed: 1.2 })
 *
 * if (isSupported) {
 *   <button onClick={() => isSpeaking ? stop() : speak("Hello world")}>
 *     {isSpeaking ? "Stop" : "Speak"}
 *   </button>
 * }
 * ```
 */
export function useTextToSpeech(voiceSettings?: VoiceSettings): UseTextToSpeechReturn {
  const [isSpeaking, setIsSpeaking] = useState(false)

  /**
   * Check if browser supports speech synthesis
   */
  const isSupported = typeof window !== 'undefined' && 'speechSynthesis' in window

  /**
   * Start speaking the provided text
   */
  const speak = useCallback((text: string) => {
    if (!isSupported || !text.trim()) return

    // Stop any current speech first
    window.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)

    // Apply voice settings
    utterance.rate = voiceSettings?.speed ?? 1
    utterance.pitch = voiceSettings?.pitch ?? 1

    // Set voice if specified
    if (voiceSettings?.voice) {
      const voices = window.speechSynthesis.getVoices()
      const selectedVoice = voices.find(v => v.name === voiceSettings.voice)
      if (selectedVoice) {
        utterance.voice = selectedVoice
      }
    }

    // Event handlers
    utterance.onstart = () => setIsSpeaking(true)
    utterance.onend = () => setIsSpeaking(false)
    utterance.onerror = () => setIsSpeaking(false)

    window.speechSynthesis.speak(utterance)
  }, [isSupported, voiceSettings?.speed, voiceSettings?.pitch, voiceSettings?.voice])

  /**
   * Stop any current speech
   */
  const stop = useCallback(() => {
    if (!isSupported) return
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }, [isSupported])

  /**
   * Cleanup on unmount - stop any ongoing speech
   */
  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel()
      }
    }
  }, [isSupported])

  return { speak, stop, isSpeaking, isSupported }
}
